import React, { FC, useMemo } from 'react';
import { Row, Col, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { AmountField, Card, Space, StatusField, TextField } from '~/components';
import { Payroll, PayrollPayment } from '~/types';
import { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';
import theme from '~/theme';
import layout from '~/hooks/layout';
import GridLayout from '~/layout/GridLayout';
import AccountNumber from './AccountNumber';
import ValidationStatusField from './ValidationStatusField';

type StatusType = 'validationStatus' | 'processingStatus';
type ProcessType = 'create' | 'view';

interface Props {
  data: Partial<Payroll>;
  className?: string;
  withScroll?: boolean;
  processType?: ProcessType;
  footer?: any;
}

const PayrollPaymentsTable: FC<Props> = (props) => {
  const { data, className, withScroll, processType } = props;

  const { instructedAmount } = data ?? {};

  const isMobile = layout.useIsMobile();

  const isPayrollCreationProcess = getIsPayrollCreationProcess(processType);
  const statusType = getStatusType(processType);

  const [t] = useTranslation('payrolls');
  const columns = useMemo<ColumnsType<PayrollPayment>>(
    () => [
      ...(isPayrollCreationProcess
        ? [
            {
              title: t('Row #'),
              dataIndex: 'rowNumber',
              render: (rowNumber: number) => rowNumber,
              width: '70px',
            },
          ]
        : []),
      {
        title: t('Account number'),
        dataIndex: 'accountNumber',
        render: (_, record) => (
          <AccountNumber accountNumber={record?.accountNumber} phoneNumber={record?.phoneNumber} />
        ),
      },
      {
        title: t('Amount'),
        dataIndex: 'amount',
        render: (amount) => <AmountField amount={amount} />,
      },
      {
        title: t('Name'),
        dataIndex: 'beneficiaryName',
        ellipsis: {
          showTitle: false,
        },
        render: (name) => name,
      },
      {
        title: t('Transfer status'),
        dataIndex: statusType,
        render: (status) =>
          statusType === 'validationStatus' ? (
            <ValidationStatusField status={status} />
          ) : (
            <StatusField data={status} addLabel={false} />
          ),
      },
    ],
    [isPayrollCreationProcess, statusType, t]
  );

  if (isMobile)
    return (
      <>
        <Space
          direction="vertical"
          size={16}
          style={{ overflow: 'auto', height: withScroll ? 'calc(100vh - 440px)' : undefined }}
        >
          {data?.records?.map((item) => {
            return <PayrollPaymentCard processType={processType} key={item.id} record={item} />;
          })}
        </Space>
        <Card
          style={{ marginTop: 16, paddingBottom: 'unset' }}
          title={
            <GridLayout xs={12}>
              <TextField label={t('Total')}>
                <AmountField amount={instructedAmount} />
              </TextField>
            </GridLayout>
          }
        />
      </>
    );
  return (
    <ContainerTable>
      <Table
        rowKey={({ id }) => id}
        className={className}
        columns={columns}
        dataSource={data?.records?.sort((p1) => (p1.validationStatus === 'OK' ? 1 : -1))}
        pagination={false}
        scroll={withScroll ? { y: 'calc(100vh - 450px)' } : undefined}
      />
      <TotalRow>
        <Typography.Text strong>{t('Total')}</Typography.Text>
        <Typography.Text className="AmountField">
          <AmountField amount={instructedAmount} />
        </Typography.Text>
      </TotalRow>
    </ContainerTable>
  );
};

interface PayrollPaymentCardProps {
  record: PayrollPayment;
  processType?: ProcessType;
}

const PayrollPaymentCard: FC<PayrollPaymentCardProps> = (props) => {
  const { record, processType } = props;
  const { t } = useTranslation('payrolls');
  const isPayrollCreationProcess = getIsPayrollCreationProcess(processType);
  const statusType = getStatusType(processType);

  return (
    <Container>
      <Card
        title={
          <Row>
            {isPayrollCreationProcess ? (
              <Col flex={2}>
                <TextField label={t('Row #')}>{record.rowNumber}</TextField>
              </Col>
            ) : null}
            <Col flex={5}>
              <TextField label={t('Name')}>{record.beneficiaryName}</TextField>
            </Col>
            <Col flex={5}>
              {statusType === 'validationStatus' ? (
                <TextField label={t('Transfer status')}>
                  <ValidationStatusField status={record.validationStatus} />
                </TextField>
              ) : (
                <StatusField data={record?.processingStatus} label={t('Transfer status')} />
              )}
            </Col>
          </Row>
        }
        expandable
      >
        <Space size={16} direction="vertical">
          <Row>
            {record?.phoneNumber && (
              <Col flex={6}>
                <TextField label={t('Phone number')}>{record?.phoneNumber}</TextField>
              </Col>
            )}
            <Col flex={6}>
              <TextField label={t('Account number')}>{record.accountNumber}</TextField>
            </Col>
          </Row>
          <TextField label={t('Amount')}>
            <AmountField amount={record.amount} />
          </TextField>
        </Space>
      </Card>
    </Container>
  );
};

const getIsPayrollCreationProcess = (processType?: ProcessType) => processType === 'create';
const getStatusType = (processType?: ProcessType): StatusType =>
  getIsPayrollCreationProcess(processType) ? 'validationStatus' : 'processingStatus';

export default styled(PayrollPaymentsTable)`
  .ant-table {
    border-radius: 0;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: none !important;
  }

  .ant-table-container {
    padding: 8px 16px 0;
  }

  .ant-table-cell {
    padding: 8px 16px 8px 0;
  }

  .ant-table-cell::before {
    content: unset !important;
  }

  .ant-table td {
    white-space: nowrap;
    line-height: 1.2;
  }

  .ant-table-body {
    overflow-y: auto !important;
  }
`;

const TotalRow = styled.div`
  width: 100%;
  background-color: #fff;
  padding: 16px 16px;
  display: flex;
  border-top: 1px solid ${theme.borderColor};

  .AmountField {
    margin-left: 37px;
  }
`;

const ContainerTable = styled.div`
  border: 1px solid ${theme.borderColor};
  border-radius: ${theme.borderRadiusBase}px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const Container = styled.div`
  border: 1px solid ${theme.borderColor};
  border-radius: ${theme.borderRadiusBase}px;
`;
